import React, { useState, useEffect } from 'react'
import {
	Dialog,
	DialogContent,
	DialogTitle,
	DialogDescription,
} from "@/components/ui/dialog"
import { ZoomIn, ZoomOut, Loader2, ExternalLink } from 'lucide-react'

interface ImageViewerProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	imageUrl: string
	altText?: string
}

type ViewMode = 'fit' | 'original'

export function ImageViewer(props: ImageViewerProps) {
	const [viewMode, setViewMode] = useState<ViewMode>('fit')
	const [imageLoaded, setImageLoaded] = useState(false)
	const [imageDimensions, setImageDimensions] = useState<{ width: number; height: number } | null>(null)
	const [error, setError] = useState(false)

	const MAX_DIMENSION = 4000

	useEffect(() => {
		if (props.open) {
			setImageLoaded(false)
			setError(false)
			setImageDimensions(null)
		}
	}, [props.open, props.imageUrl])

	const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
		const img = e.currentTarget
		const width = img.naturalWidth
		const height = img.naturalHeight

		setImageDimensions({ width, height })
		setImageLoaded(true)

		const maxViewportWidth = window.innerWidth * 0.9
		const maxViewportHeight = window.innerHeight * 0.9

		if (width <= maxViewportWidth && height <= maxViewportHeight)
			setViewMode('original')
		else
			setViewMode('fit')
	}

	const handleImageError = () => {
		setError(true)
		setImageLoaded(true)
	}

	const toggleViewMode = () => {
		setViewMode(prev => prev === 'fit' ? 'original' : 'fit')
	}

	const handleDownload = () => {
		const a = document.createElement('a')
		a.href = props.imageUrl
		a.download = props.altText || 'image'
		a.target = '_blank'
		document.body.appendChild(a)
		a.click()
		document.body.removeChild(a)
	}

	const isFitMode = viewMode === 'fit'
	const showToggleButton = imageDimensions && (
		imageDimensions.width > window.innerWidth * 0.9 ||
		imageDimensions.height > window.innerHeight * 0.9
	)

	const cursorClass = showToggleButton
		? (isFitMode ? 'cursor-zoom-in' : 'cursor-zoom-out')
		: ''

	const getImageStyle = (): React.CSSProperties => {
		if (!imageDimensions)
			return {}

		if (viewMode === 'fit') {
			return {
				maxWidth: 'calc(90vw - 24px)',
				maxHeight: 'calc(90vh - 70px)',
				width: 'auto',
				height: 'auto',
				objectFit: 'contain'
			}
		}

		const clampedWidth = Math.min(imageDimensions.width, MAX_DIMENSION)
		const clampedHeight = Math.min(imageDimensions.height, MAX_DIMENSION)

		return {
			maxWidth: `${clampedWidth}px`,
			maxHeight: `${clampedHeight}px`,
			width: 'auto',
			height: 'auto'
		}
	}

	const getContainerStyle = (): React.CSSProperties => {
		if (viewMode === 'original' && imageDimensions) {
			return {
				maxWidth: 'calc(90vw - 24px)',
				maxHeight: 'calc(90vh - 70px)',
				overflowX: 'auto',
				overflowY: 'auto',
				width: 'fit-content',
				height: 'fit-content'
			}
		}

		return {}
	}

	return (
		<Dialog open={props.open} onOpenChange={props.onOpenChange}>
			<DialogContent className="p-0 border-0 bg-white/95 shadow-none max-w-none w-auto">
				<DialogTitle className="sr-only">Image viewer</DialogTitle>
				<DialogDescription className="sr-only">Image attachment</DialogDescription>
				{!imageLoaded && !error && (
					<div className="flex items-center justify-center min-w-[200px] min-h-[200px]">
						<Loader2 className="h-8 w-8 animate-spin text-black" />
					</div>
				)}

				{error && (
					<div className="flex items-center justify-center min-w-[200px] min-h-[200px] text-black p-8">
						<p>Failed to load image</p>
					</div>
				)}

				{!error && (
					<div className="flex flex-col gap-3 p-3">
						{imageLoaded && (
							<div className="flex justify-between items-center gap-2">
								<div className="flex gap-2">
									{showToggleButton && (
										<button
											type="button"
											onClick={toggleViewMode}
											className="bg-black/10 hover:bg-black/20 text-black rounded-lg p-2 transition-colors"
											title={isFitMode ? 'View original size' : 'Fit to screen'}
										>
											{isFitMode ? (
												<ZoomIn className="h-5 w-5" />
											) : (
												<ZoomOut className="h-5 w-5" />
											)}
										</button>
									)}

									<button
										type="button"
										onClick={handleDownload}
										className="bg-black/10 hover:bg-black/20 text-black rounded-lg p-2 transition-colors"
										title="Open in new tab"
									>
										<ExternalLink className="h-5 w-5" />
									</button>
								</div>
							</div>
						)}

						<div style={getContainerStyle()}>
							<img
								src={props.imageUrl}
								alt={props.altText || 'Image'}
								className={`rounded-lg ${cursorClass} ${!imageLoaded ? 'hidden' : ''}`}
								style={getImageStyle()}
								onLoad={handleImageLoad}
								onError={handleImageError}
								onClick={showToggleButton ? toggleViewMode : undefined}
								draggable={false}
							/>
						</div>
					</div>
				)}
			</DialogContent>
		</Dialog>
	)
}
